/// <reference types="cordova-plugin-statusbar"/>
import { createTheme, lighten } from '@mui/material';
import memoizeOne from 'memoize-one';
import { baseThemeColor } from './base-color';
import { EffectiveThemeType } from '../types';
import { getEffectiveThemeMode } from './dark-theme';
import { getButtonVariants, getIconButtonVariants } from './components/button-variants';
import { getChipVariants } from './components/chip-variants';
import { getRowVariants } from './components/row-variants';
import branding from 'shared/app/model/branding';
import { getButtonGroupVariants } from 'console/theme/styles/components/button-group-variants';
import { useEffect } from 'react';
import { isMobileApp } from 'webui/utils/version';
import { getListButtonVariants, getListVariants, getSecondaryActionsVariants, } from 'console/theme/styles/components/list-item-variants';
import { waitForCordovaReady } from 'shared/app/lib/wait-for-cordova';
import { baxi_theme_fonts } from 'console/theme/styles/colors/brands';
const _createTheme = (dark_mode, brand_code, user_theme) => {
    const palette = baseThemeColor(dark_mode, brand_code, user_theme);
    const custom_theme = user_theme?.[dark_mode ? 'dark' : 'light'];
    return createTheme({
        palette,
        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    '*, *:before, *:after': {
                        boxSizing: 'border-box',
                    },
                    '*': {
                        '.swiper-slide': {
                            '*::-webkit-scrollbar': {
                                display: 'none',
                            },
                            '*': {
                                scrollbarWidth: 'none',
                            },
                        },
                        '&::-webkit-scrollbar': {
                            width: 5,
                            height: 5,
                            backgroundColor: 'transparent',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: palette.primary.main,
                            borderRadius: 10,
                        },
                        scrollbarColor: `${palette.primary.main} transparent`,
                    },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        backgroundImage: 'none',
                    },
                },
            },
            MuiButton: {
                variants: getButtonVariants(palette),
            },
            MuiButtonGroup: {
                variants: getButtonGroupVariants(palette),
            },
            MuiIconButton: {
                variants: getIconButtonVariants(palette),
            },
            MuiChip: {
                variants: getChipVariants(palette),
            },
            MuiTableRow: {
                variants: getRowVariants(palette),
            },
            MuiFormControlLabel: {
                styleOverrides: {
                    root: {
                        '& .MuiSwitch-root': {
                            marginLeft: '2px',
                        },
                    },
                },
            },
            MuiAutocomplete: {
                styleOverrides: {
                    root: {
                        '& .MuiOutlinedInput-root': {
                            padding: '8.5px 15px',
                        },
                        '& .MuiOutlinedInput': {
                            padding: '8.5px 15px',
                        },
                    },
                },
            },
            MuiListItemButton: {
                variants: getListButtonVariants(palette),
                styleOverrides: {
                    root: {
                        '&.Mui-selected': {
                            background: custom_theme?.primary ?? branding.colors.main,
                            color: '#fff',
                            '&:hover': {
                                background: lighten(custom_theme?.primary ?? branding.colors.main, 0.2),
                            },
                        },
                    },
                },
            },
            MuiListItem: {
                variants: getListVariants(palette),
            },
            MuiListItemSecondaryAction: {
                variants: getSecondaryActionsVariants(palette),
            },
            MuiInputBase: {
                styleOverrides: {
                    root: {
                        padding: '8.5px 15px',
                        input: {
                            padding: '0px',
                        },
                    },
                },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        padding: '8.5px 15px',
                        input: {
                            '&[type=number]': {
                                MozAppearance: 'textfield',
                            },
                            '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                                WebkitAppearance: 'none',
                                margin: 0,
                            },
                        },
                        '& .MuiAutocomplete-input': {
                            padding: '0 !important',
                        },
                    },
                },
            },
            MuiButtonBase: {
                styleOverrides: {
                    root: {
                        '&.MuiPhoneNumber-flagButton': {
                            height: '1em',
                            width: '1em',
                        },
                    },
                },
            },
            MuiSelect: {
                styleOverrides: {
                    select: {
                        padding: '0px',
                    },
                },
            },
            MuiFormHelperText: {
                styleOverrides: {
                    root: {
                        '&.Mui-required': {
                            color: palette.warning.main,
                        },
                    },
                },
            },
        },
        // #if brand.code == 'baxi'
//         typography: {
//             ...baxi_theme_fonts,
//         },
        // #endif
    });
};
const memoizedCreateTheme = memoizeOne(_createTheme);
const getTheme = (effective_theme, brand_code, user_theme) => memoizedCreateTheme(effective_theme == EffectiveThemeType.DARK, brand_code, user_theme);
export const baseTheme = (theme_type, brand_code, user_theme) => {
    return getTheme(getEffectiveThemeMode(theme_type), brand_code, user_theme);
};
function _changeStatusBarColor(theme, brand_code) {
    const is_dark = theme.palette.mode === 'dark';
    if (brand_code === 'baxi') {
        if (is_dark) {
            StatusBar?.backgroundColorByHexString(theme.palette.background.default);
            StatusBar?.styleLightContent(); // set light text
        }
        else {
            StatusBar?.backgroundColorByHexString(theme.palette.primary.main);
            StatusBar?.styleLightContent();
        }
        return;
    }
    if (is_dark) {
        StatusBar?.backgroundColorByHexString(theme.palette.background.default);
        StatusBar?.styleLightContent(); // set light text
    }
    else {
        StatusBar?.backgroundColorByHexString(theme.palette.background.default);
        StatusBar?.styleDefault(); //set dark text
    }
}
export const useBaseTheme = (theme_type, brand_code, user_theme = {}) => {
    const effective_mode = getEffectiveThemeMode(theme_type);
    const theme = getTheme(effective_mode, brand_code, user_theme);
    useEffect(() => {
        (async () => {
            if (isMobileApp()) {
                await waitForCordovaReady();
                _changeStatusBarColor(theme, brand_code);
            }
        })();
    }, [effective_mode]);
    return theme;
};
